/**
 * Fait : les appels du client qui portent sur les DOCUMENTS d'un projet —
 *   déposer (avec sa progression), lister, lire le texte extrait, supprimer.
 *
 *   LE DÉPÔT NE PASSE PAS PAR `appelle` : c'est le seul appel du produit dont
 *   on veut savoir où il en est pendant qu'il se fait, et `fetch` ne le dit
 *   pas. Il emprunte `envoi-avec-progression.ts`, qui rend le MÊME type
 *   d'issue et partage la même traduction des réponses.
 *
 * Ne fait pas : il ne décide d'aucun droit, n'affiche rien, et ne devine aucun
 *   format — celui-ci est DÉCLARÉ au serveur, qui ne le lit jamais dans un nom.
 */

import type { FormatDeFichier, EtatDeFichier } from '@aymoai/shared/donnees';

import { appelle } from './appel.ts';
import { envoieAvecProgression } from './envoi-avec-progression.ts';

/** Un document tel que la liste le rend — jamais son texte, qui pèse. */
export interface DocumentVu {
  readonly id: string;
  readonly nomFichier: string;
  readonly format: FormatDeFichier;
  readonly tailleOctets: number;
  readonly etat: EtatDeFichier;
  readonly pages?: number;
  readonly natureDEchec?: string;
  readonly version: number;
}

/** Le texte extrait d'un document, et ce qu'il faut en savoir. */
export interface TexteDuDocument {
  readonly texte: string;
  readonly pages: number;

  /** Vrai quand le texte a été COUPÉ : le dire vaut mieux que de le cacher. */
  readonly coupe: boolean;
}

/** Les documents d'un projet, le dernier déposé en tête. */
export function listeLesDocuments(projetId: string) {
  return appelle<{ fichiers: DocumentVu[] }>(`/api/projets/${projetId}/fichiers`);
}

/** Le texte d'un document — le serveur refuse tant qu'il n'est pas prêt. */
export function litLeTexteDuDocument(projetId: string, fichierId: string) {
  return appelle<TexteDuDocument>(`/api/projets/${projetId}/fichiers/${fichierId}/texte`);
}

/**
 * Supprime un document : les octets partent du volume, la place revient au
 * projet. Rien ne le rattrape — c'est pourquoi l'écran passe par une fenêtre
 * de confirmation avant d'arriver ici.
 */
export function supprimeLeDocument(projetId: string, fichierId: string) {
  return appelle<void>(`/api/projets/${projetId}/fichiers/${fichierId}`, { methode: 'DELETE' });
}

/**
 * Dépose un document et annonce son avancée.
 *
 * Le nom et le format voyagent en paramètres, les octets en corps brut : c'est
 * exactement ce que la route attend, et ce qu'un navigateur sait envoyer d'un
 * `File` sans aucune bibliothèque.
 */
export function deposeUnDocument(
  projetId: string,
  fichier: File,
  format: FormatDeFichier,
  surProgression: (part: number) => void,
) {
  const nom = encodeURIComponent(fichier.name);

  return envoieAvecProgression<{ fichierId: string }>(
    `/api/projets/${projetId}/fichiers?nom=${nom}&format=${format}`,
    { methode: 'POST', octets: fichier, surProgression },
  );
}
